// The Monday executive page, in the app: every enabled practice's ledger review row.
//
// super_admin only (the sidebar shows it to nobody else and the backend refuses
// everyone else). Reads GET /api/ledger-review/exec, which is billing-ops'
// exec-summary.json with each practice's notes merged in and the rows sorted as the
// PDF page sorted them: trust descending, then rate descending, failures last. The
// bullets under the title and the footer are the page's own wording. Cross-practice
// like Mission Control, so it takes no selectedPractice.
//
// Counts, rates and dollar totals only — nothing here names a patient.

import React, { useCallback, useEffect, useState } from "react";
import "../features/ledgerReview/LedgerReview.css";
import { ExecRowCells, formatStamp } from "../features/ledgerReview/execCells";

const API_URL = process.env.REACT_APP_API_URL || "";

export function reportingDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((p) => parseInt(p, 10));
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function ExecReviewPage() {
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/ledger-review/exec`, {
        credentials: "include",
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          (body && body.error) || "Could not load the executive review",
        );
      }
      if (!body || !Array.isArray(body.practices)) {
        throw new Error("The response could not be read. Try again.");
      }
      setPage(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="exec-review-page" data-testid="exec-review-page">
      <div className="exec-review-header">
        <div>
          <h1>Weekly Ledger Review: All Practices</h1>
          <p className="mc-muted">
            Every enabled practice, computed from the live ledger. The demo
            practice is left out.
          </p>
        </div>
        {page && (
          <div className="exec-review-meta">
            Reporting Date
            <strong>{reportingDate(page.as_of)}</strong>
            <span data-testid="exec-review-stamp">
              Computed {formatStamp(page.computed_at)}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="exec-review-error" role="alert">
          {error}
          <button type="button" className="exec-review-retry" onClick={load}>
            Retry
          </button>
        </div>
      )}
      {loading && !error && (
        <p className="mc-muted">Computing every practice&apos;s review…</p>
      )}

      {page && !error && (
        <>
          <ul className="exec-review-how">
            {page.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <table className="lr-table" data-testid="exec-review-table">
            <colgroup>
              {["practice", "rate", "trust", "why", "what"].map((c) => (
                <col key={c} className={c} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {page.columns.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {page.practices.map((row) => (
                <tr
                  key={row.practice_id}
                  data-testid={`exec-review-row-${row.practice_id}`}
                >
                  <ExecRowCells row={row} />
                </tr>
              ))}
            </tbody>
          </table>
          <div className="exec-review-foot">
            {page.footer} {page.window_note}
          </div>
        </>
      )}
    </div>
  );
}
